import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth-context";
import { PERMISSION_MATRIX, getDefaultAllowed, type PermissionRole } from "@/lib/permissions-matrix";

interface RolePermissionOverride {
  id: string;
  tenantId: string;
  permissionKey: string;
  role: string;
  allowed: boolean;
}

interface PermissionsContextValue {
  hasPermission: (section: string, label: string, role: string) => boolean;
  overrides: RolePermissionOverride[];
  isLoading: boolean;
  isError: boolean;
}

const PermissionsContext = createContext<PermissionsContextValue>({
  hasPermission: (section, label, role) => {
    if (role === "super_admin") return true;
    return getDefaultAllowed(section, label, role as PermissionRole);
  },
  overrides: [],
  isLoading: false,
  isError: false,
});

export function PermissionsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const effectiveTenantId = user?.impersonating?.tenantId || user?.tenantId;

  const {
    data: overrides = [],
    isLoading,
    isError,
  } = useQuery<RolePermissionOverride[]>({
    queryKey: ["/api/role-permissions", effectiveTenantId, user?.role],
    queryFn: async () => {
      const response = await fetch("/api/role-permissions", {
        credentials: "include",
        cache: "no-store",
        headers: { "Cache-Control": "no-cache" },
      });
      if (!response.ok) throw new Error(`Failed to load permissions (${response.status})`);
      return response.json() as Promise<RolePermissionOverride[]>;
    },
    enabled: !!user,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  });

  const overrideMap = useMemo(() => {
    const m = new Map<string, boolean>();
    for (const o of overrides) {
      m.set(`${o.permissionKey}:${o.role}`, o.allowed);
    }
    return m;
  }, [overrides]);

  const hasPermission = useMemo(() => {
    return (section: string, label: string, role: string): boolean => {
      // super_admin is unrestricted
      if (role === "super_admin") return true;
      // tenant_admin access is determined purely by the matrix — not overridable
      if (role === "tenant_admin") return getDefaultAllowed(section, label, "tenant_admin");
      // Do not fall back to defaults while a restricted user's permissions are
      // loading or failed. A stale/default grant would leak disabled features.
      if (isLoading || isError) return false;
      // supervisor / agent: stored overrides take priority, then fall back to matrix
      const key = `${section}:${label}`;
      const overrideKey = `${key}:${role}`;
      if (overrideMap.has(overrideKey)) {
        return overrideMap.get(overrideKey)!;
      }
      return getDefaultAllowed(section, label, role as PermissionRole);
    };
  }, [overrideMap, isError, isLoading]);

  const value = useMemo(
    () => ({ hasPermission, overrides, isLoading, isError }),
    [hasPermission, overrides, isError, isLoading],
  );

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  return useContext(PermissionsContext);
}

export function buildDefaultPermissionState(): Map<string, boolean> {
  const map = new Map<string, boolean>();
  for (const section of PERMISSION_MATRIX) {
    for (const item of section.items) {
      for (const role of ["supervisor", "agent"] as PermissionRole[]) {
        const key = `${section.section}:${item.label}:${role}`;
        map.set(key, item.roles.includes(role));
      }
    }
  }
  return map;
}
